// ─── KPIs financiers (Google Analytics 4) ────────────────────────────────────
//
// Instrumente le tunnel de paiement pour produire, dans GA4, des indicateurs
// exploitables par des investisseurs :
//   • begin_checkout        → nombre de TENTATIVES de paiement
//   • add_payment_info      → intention confirmée (push envoyé / redirection)
//   • purchase              → REVENU (événement e-commerce standard GA4 : value,
//                             currency, transaction_id) → alimente les rapports
//                             Monétisation nativement
//   • payment_failed/…      → échecs, annulations, expirations (dénominateurs)
//   • subscription_started  → nouvel abonné (acquisition)
//   • subscription_renewed  → renouvellement (rétention)
//
// KPIs dérivables dans GA4 :
//   Taux de conversion paiement = purchase / begin_checkout
//   Taux d'échec               = payment_failed / begin_checkout
//   Taux de renouvellement     = subscription_renewed /
//                                (subscription_started + subscription_renewed)
//
// Best-effort : trackEvent avale toute erreur, ne casse jamais le paiement.
//
// Parcours d'achat GA4 (Monétisation → Parcours d'achat), segmentable par
// « Catégorie de l'appareil » (device_category) :
//   view_item (vue d'un plan) → add_to_cart (plan choisi) → begin_checkout
//   (tentative) → purchase (revenu).
import { trackEvent, deviceCategory } from '@/lib/analytics';
import { PaymentPlan } from '../types';
import { usePaymentStore } from '../state/usePaymentStore';

/** Item e-commerce GA4 standard pour un plan d'abonnement. */
function planItem(plan: Pick<PaymentPlan, 'id' | 'name' | 'price'>) {
  return {
    item_id: plan.id,
    item_name: plan.name,
    item_category: 'subscription',
    price: plan.price,
    quantity: 1,
  };
}

/** Paramètres communs déduits de la tentative de paiement en cours. */
function attemptContext(): Record<string, unknown> | null {
  const { attempt, plans } = usePaymentStore.getState();
  if (!attempt) return null;
  const plan = plans.find((p) => p.id === attempt.planId);
  return {
    transaction_id: attempt.reference,
    order_number: attempt.orderNumber ?? undefined,
    plan_id: attempt.planId,
    plan_name: plan?.name ?? attempt.planId,
    provider: attempt.providerId,
    payment_method: attempt.method,
    value: plan?.price ?? 0,
    currency: plan?.currency ?? 'USD',
    device_category: deviceCategory(),
    items: plan
      ? [planItem(plan)]
      : [{ item_id: attempt.planId, item_name: attempt.planId, item_category: 'subscription', quantity: 1 }],
  };
}

/**
 * Vue de l'écran des plans : `view_item_list` + un `view_item` par plan (dont
 * le plan Premium → « combien ont essayé de voir le plan premium »).
 */
export function trackPlansViewed(plans: PaymentPlan[]): void {
  const dc = deviceCategory();
  trackEvent('view_item_list', {
    item_list_name: 'subscription_plans',
    device_category: dc,
    items: plans.map(planItem),
  });
  for (const plan of plans) {
    trackEvent('view_item', {
      device_category: dc,
      currency: plan.currency,
      value: plan.price,
      plan_id: plan.id,
      plan_name: plan.name,
      items: [planItem(plan)],
    });
  }
}

/** Plan ajouté au panier (l'utilisateur a cliqué « Choisir ce plan »). */
export function trackAddToCart(plan: PaymentPlan): void {
  trackEvent('add_to_cart', {
    device_category: deviceCategory(),
    currency: plan.currency,
    value: plan.price,
    plan_id: plan.id,
    plan_name: plan.name,
    items: [planItem(plan)],
  });
}

/** Tentative de paiement démarrée (dénominateur du taux de conversion). */
export function trackCheckoutStarted(): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('begin_checkout', ctx);
}

/** Moyen de paiement engagé : push mobile money envoyé ou redirection carte. */
export function trackPaymentInfoAdded(): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('add_payment_info', ctx);
}

/**
 * Paiement confirmé → REVENU. Émet `purchase` (standard GA4, alimente les
 * rapports de monétisation) + `subscription_started`/`subscription_renewed`
 * pour le taux de renouvellement.
 */
export function trackPurchase(isRenewal: boolean): void {
  const ctx = attemptContext();
  if (!ctx) return;
  trackEvent('purchase', {
    ...ctx,
    payment_type: ctx.payment_method,
    subscription_type: isRenewal ? 'renewal' : 'new',
    is_renewal: isRenewal,
    items: [
      {
        item_id: ctx.plan_id,
        item_name: ctx.plan_name,
        item_category: 'subscription',
        price: ctx.value,
        quantity: 1,
      },
    ],
  });
  trackEvent(isRenewal ? 'subscription_renewed' : 'subscription_started', ctx);
}

/** Paiement échoué (refusé / non abouti). */
export function trackPaymentFailed(reason?: string | null): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('payment_failed', { ...ctx, reason: reason ?? undefined });
}

/** Paiement annulé par l'utilisateur. */
export function trackPaymentCancelled(): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('payment_cancelled', ctx);
}

/** Vérification expirée (débité potentiel non confirmé à temps). */
export function trackPaymentExpired(): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('payment_expired', ctx);
}

/** Erreur réseau/technique pendant le paiement. */
export function trackPaymentError(reason?: string | null): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('payment_error', { ...ctx, reason: reason ?? undefined });
}

/** Nouvelle tentative après un échec. */
export function trackPaymentRetry(): void {
  const ctx = attemptContext();
  if (ctx) trackEvent('payment_retry', ctx);
}
